import { computed, ref, watch } from "vue";

import {
  getSupportedLangsForMarket,
  LANG_KEY,
} from "../constants/payment";
import {
  TRANSLATIONS,
  UPI_FIELD_TRANSLATIONS,
} from "../constants/translations";

function getBrowserLanguage(supportedLangs) {
  const raw = (
    navigator.language ||
    navigator.userLanguage ||
    ""
  ).toLowerCase();

  const candidate = raw.startsWith("hi")
    ? "hi"
    : raw.startsWith("ko")
      ? "ko"
      : "en";

  return supportedLangs.includes(candidate) ? candidate : supportedLangs[0] || "en";
}

function getCurrentLang(supportedLangs) {
  try {
    const saved = localStorage.getItem(LANG_KEY);

    if (saved && supportedLangs.includes(saved)) {
      return saved;
    }
  } catch {
    return getBrowserLanguage(supportedLangs);
  }

  return getBrowserLanguage(supportedLangs);
}

export function useLocale() {
  const supportedLangs = computed(() => getSupportedLangsForMarket());
  const currentLang = ref(getCurrentLang(supportedLangs.value));

  const translations = computed(
    () => TRANSLATIONS[currentLang.value] || TRANSLATIONS.en,
  );
  const fieldTranslations = computed(
    () => UPI_FIELD_TRANSLATIONS[currentLang.value] || UPI_FIELD_TRANSLATIONS.en,
  );

  function setLanguage(lang) {
    if (!supportedLangs.value.includes(lang)) {
      return;
    }

    currentLang.value = lang;

    try {
      localStorage.setItem(LANG_KEY, lang);
    } catch {
      // Ignore localStorage errors in restricted environments.
    }
  }

  function t(key) {
    return translations.value[key] || TRANSLATIONS.en[key] || key;
  }

  function langLabel(lang) {
    if (lang === "en") return "EN";
    if (lang === "hi") return "हिंदी";
    if (lang === "ko") return "한국어";
    return lang.toUpperCase();
  }

  watch(
    currentLang,
    (lang) => {
      document.documentElement.lang = lang;
    },
    { immediate: true },
  );

  watch(
    supportedLangs,
    (langs) => {
      if (!langs.includes(currentLang.value)) {
        setLanguage(langs[0] || "en");
      }
    },
    { immediate: true },
  );

  return {
    currentLang,
    fieldTranslations,
    langLabel,
    setLanguage,
    supportedLangs,
    t,
  };
}
